import { useCallback, useEffect, useRef, useState } from 'react';

type SwipeDirection = 'up' | 'down' | 'left' | 'right';

interface UseParentWrapperSwipeOptions {
  containerRef: React.RefObject<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null>;
  onSwipe: (deltaX: number, deltaY: number, direction: SwipeDirection) => void;
  enabled: boolean;
  minSwipeDistance?: number;
}

export const useParentWrapperSwipe = ({
  containerRef,
  onSwipe,
  enabled,
  minSwipeDistance = 12,
}: UseParentWrapperSwipeOptions) => {
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!enabled) return;

      const touch = event.touches[0];
      if (touch) {
        setTouchStart({ x: touch.clientX, y: touch.clientY });
      }
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!enabled || !touchStart) return;

      const touch = event.changedTouches[0];
      if (!touch) {
        setTouchStart(null);
        return;
      }

      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (Math.max(absX, absY) < minSwipeDistance) {
        setTouchStart(null);
        return;
      }

      const direction: SwipeDirection = absX > absY
        ? (deltaX > 0 ? 'right' : 'left')
        : (deltaY > 0 ? 'down' : 'up');

      onSwipe(deltaX, deltaY, direction);
      setTouchStart(null);
    },
    [enabled, touchStart, minSwipeDistance, onSwipe]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    let parentWrapper = containerRef.current.parentElement;
    while (parentWrapper && !parentWrapper.classList.contains('game-card')) {
      parentWrapper = parentWrapper.parentElement;
    }

    if (!parentWrapper) {
      parentWrapper = containerRef.current.closest('.container')?.parentElement || containerRef.current.parentElement;
    }

    if (parentWrapper) {
      parentWrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
      parentWrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

      return () => {
        parentWrapper?.removeEventListener('touchstart', handleTouchStart);
        parentWrapper?.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [containerRef, handleTouchStart, handleTouchEnd]);
};

